/**
 * YUMIX — i18n bootstrap (react-i18next + i18next-browser-languagedetector).
 *
 * Resolution chain for the active language (Iter. 391 — DB-first hard rule):
 *   1. 🔒 `localStorage["user_profile"].language` — the AUTHENTICATED user's
 *      DB-persisted preference. Cached on login from `/api/auth/me`, this
 *      reflects the choice they saved in Settings on ANY device. As long as
 *      a user is logged in, this WINS — navigator.language never re-overrides
 *      it. This is the "user choice = king" rule.
 *   2. localStorage["i18nextLng"] / legacy localStorage["language"]
 *      (anonymous user's explicit choice via Settings/locale toggle before
 *      account creation — e.g., a visitor flipping the language on the landing
 *      page before signing up).
 *   3. `querystring` (?lng=es) — marketing campaigns and deep links.
 *   4. `navigator.language` — the device/browser language.
 *      🔑 ORGANIC Play Store install path: a Spanish user installing YUMIX
 *      from the ES Play Store opens the app directly in Spanish. This now
 *      ONLY fires for truly anonymous visitors (no user_profile, no prior
 *      explicit choice) — i.e., during onboarding bootstrap.
 *   5. `htmlTag` — last-resort signal from the `<html lang>` attribute.
 *   6. Fallback: French (YUMIX is a Quebec-born product).
 *
 * NOTE: this module DOES NOT replace the legacy `useLanguage()` context. Both
 * coexist during the migration: `LanguageProvider` keeps `language` in sync
 * with i18next so the >180 files using inline `language === 'fr' ? …` still
 * work unmodified, while new code can use `useTranslation()` from react-i18next.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import fr from './locales/fr.json';
import en from './locales/en.json';
import es from './locales/es.json';

export const SUPPORTED_LANGUAGES = ['fr', 'en', 'es'];
export const DEFAULT_LANGUAGE = 'fr';

// Legacy code stored the language under "language". The detector defaults to
// "i18nextLng". We migrate by checking both — newest wins, but if i18nextLng
// is empty and the legacy key exists, we seed i18next from it so users don't
// see their language reset on first load after the migration.
function bootstrapLegacyKey() {
  try {
    const legacy = localStorage.getItem('language');
    const fresh = localStorage.getItem('i18nextLng');
    if (legacy && !fresh && SUPPORTED_LANGUAGES.includes(legacy)) {
      localStorage.setItem('i18nextLng', legacy);
    }
  } catch (_) { /* SSR / no-storage envs */ }
}
// Bootstrap WITHOUT crashing on bad localStorage / poisoned values.
// If any of this throws, the app still boots — just in French fallback.
try {
  bootstrapLegacyKey();
} catch (_) { /* hostile env / private mode */ }

// Sanity-check the persisted i18nextLng value. If it's malformed (e.g.,
// stale "fr-CA" format from a pre-Lot-#11 build, OR completely garbage from
// a corrupted SW cache), reset it. This prevents the dreaded "white screen
// of death" on PWA updates where the user has a poisoned localStorage state.
try {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('i18nextLng') || '';
    if (stored && !/^[a-z]{2}(-[A-Z]{2})?$/.test(stored)) {
      // eslint-disable-next-line no-console
      console.warn('[i18n] Clearing poisoned i18nextLng:', stored);
      localStorage.removeItem('i18nextLng');
    }
  }
} catch (_) { /* noop */ }

try {
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {
        fr: { translation: fr },
        en: { translation: en },
        es: { translation: es },
      },
      supportedLngs: SUPPORTED_LANGUAGES,
      // 🔑 PLAY STORE ORGANIC FIX: navigator.language returns "es-ES" / "fr-CA" /
      // "en-GB" with region codes. Without this, i18next would NOT match those
      // to our supported `["fr", "en", "es"]` list and would silently fall back
      // to French. Stripping the region ensures a Spanish phone (es-ES, es-MX,
      // es-AR, ca-ES...) opens YUMIX directly in Spanish.
      load: 'languageOnly',
      nonExplicitSupportedLngs: true,
      fallbackLng: {
        // Spanish users who hit a missing key fall back to English (NEVER French
        // — that would be incomprehensible). English users fall back to French
        // legacy strings. French is the source of truth.
        es: ['en', 'fr'],
        en: ['fr'],
        default: ['fr'],
      },
      interpolation: {
        escapeValue: false, // React already escapes
      },
      detection: {
        // PRIORITY ORDER (Iter. 391 — DB-first rule):
        //   1. userProfile  — authenticated user's DB-persisted language (KING)
        //   2. localStorage — anonymous user's explicit choice (pre-account)
        //   3. querystring  — marketing campaigns / deep links (?lng=es)
        //   4. navigator    — device/browser language (organic onboarding only)
        //   5. htmlTag      — last-resort signal
        order: ['userProfile', 'localStorage', 'querystring', 'navigator', 'htmlTag'],
        lookupQuerystring: 'lng',
        lookupLocalStorage: 'i18nextLng',
        // 🚫 We DO NOT cache to localStorage automatically anymore. The legacy
        // i18nextLng key is still WRITTEN by AppContext.changeLanguage() for
        // backward-compat readers, but auto-caching here would let an OS-level
        // detection silently seed i18nextLng and then masquerade as an "explicit
        // user choice" on reload. The user_profile detector + AppContext writes
        // are the only sources of truth.
        caches: [],
      },
      // Avoid emitting noisy console messages in production
      debug: false,
      returnNull: false,
      returnEmptyString: false,
    });

  // 🔒 USER PROFILE DETECTOR (Iter. 391)
  // Reads the authenticated user's DB-cached language from localStorage.
  // Placed FIRST in the detection chain, so a logged-in user's choice WINS
  // before navigator.language is ever consulted. This kills the cold-boot
  // flicker where the UI showed FR briefly while AppContext was rehydrating.
  try {
    if (i18n.services && i18n.services.languageDetector &&
        typeof i18n.services.languageDetector.addDetector === 'function') {
      i18n.services.languageDetector.addDetector({
        name: 'userProfile',
        lookup() {
          try {
            const raw = localStorage.getItem('user_profile');
            if (!raw) return undefined;
            const profile = JSON.parse(raw);
            const lng = profile && profile.language
              ? String(profile.language).slice(0, 2).toLowerCase()
              : null;
            return SUPPORTED_LANGUAGES.includes(lng) ? lng : undefined;
          } catch (_) { return undefined; }
        },
        cacheUserLanguage() { /* read-only: user_profile is owned by AppContext */ },
      });
      // Re-resolve now that the custom detector is registered (init already ran).
      try {
        const resolved = i18n.services.languageDetector.detect(
          ['userProfile', 'localStorage', 'querystring', 'navigator', 'htmlTag']
        );
        const short = (Array.isArray(resolved) ? resolved[0] : resolved || '').slice(0, 2);
        if (SUPPORTED_LANGUAGES.includes(short) && short !== i18n.language) {
          i18n.changeLanguage(short);
        }
      } catch (_) { /* noop */ }
    }
  } catch (_) { /* noop — detector registration failed, fallback chain still works */ }
} catch (initErr) {
  // Last-resort safety net: if i18next somehow fails to boot, fall back to
  // a minimal no-op init so React can still render. The legacy useLanguage()
  // graceful fallback will keep most strings readable in FR.
  // eslint-disable-next-line no-console
  console.error('[i18n] init failed, falling back to minimal init:', initErr);
  try {
    i18n.use(initReactI18next).init({
      resources: { fr: { translation: fr } },
      lng: 'fr',
      fallbackLng: 'fr',
      interpolation: { escapeValue: false },
    });
  } catch (_) { /* noop — app continues without i18next */ }
}

// Keep <html lang="…"> in sync so screen readers and OS-level translation
// engines (Safari Reader, Chrome Translate) understand the page.
function syncHtmlLang(lng) {
  try {
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.setAttribute('lang', (lng || 'fr').slice(0, 2));
    }
  } catch (_) { /* noop */ }
}
syncHtmlLang(i18n.language);
i18n.on('languageChanged', syncHtmlLang);

// 🪞 Mirror the active language into the legacy "language" localStorage key
// (Iter. 383). Some pre-i18next code paths still read `localStorage["language"]`
// directly; keep them in sync so a Play-Store organic ES user doesn't see a
// stale FR string from an old code path.
function mirrorLegacyKey(lng) {
  try {
    const short = (lng || 'fr').slice(0, 2);
    if (['fr', 'en', 'es'].includes(short)) {
      localStorage.setItem('language', short);
    }
  } catch (_) { /* private mode */ }
}
mirrorLegacyKey(i18n.language);
i18n.on('languageChanged', mirrorLegacyKey);

// ─────────────────────────────────────────────────────────────────────
// 📡 SENTINELLE TRACKING — log every first-load detection so Yuri can
// see the organic Play Store breakdown on his Admin Dashboard.
// Fire-and-forget POST to /api/telemetry/language-detected. Silent on errors.
// Runs ONCE per browser session (sessionStorage flag) to avoid spam.
// ─────────────────────────────────────────────────────────────────────
function trackLanguageDetection() {
  try {
    if (typeof window === 'undefined') return;
    const SESSION_FLAG = 'yumix_language_telemetry_sent';
    const alreadySent = sessionStorage.getItem(SESSION_FLAG);
    const isFirstLoad = !alreadySent;

    // Determine which detector actually won. i18next-browser-languagedetector
    // does not expose this directly — we infer it from where the resolution
    // could have come from, walking the same order we configured.
    let source = 'fallback';
    try {
      const url = new URL(window.location.href);
      const qs = url.searchParams.get('lng');
      const ls = localStorage.getItem('i18nextLng');
      const nav = (navigator.language || '').slice(0, 2);
      const resolved = (i18n.language || '').slice(0, 2);
      if (ls && ls.startsWith(resolved)) source = 'localStorage';
      else if (nav === resolved && SUPPORTED_LANGUAGES.includes(nav)) source = 'navigator';
      else if (qs && qs.startsWith(resolved)) source = 'querystring';
      else if (document.documentElement.lang === resolved) source = 'htmlTag';
    } catch (_) { /* noop */ }

    const payload = {
      detected: (i18n.language || 'fr').slice(0, 5),
      source,
      navigator_lang: (navigator.language || '').slice(0, 20),
      is_first_load: isFirstLoad,
      referrer: (document.referrer || '').slice(0, 512),
    };

    const apiBase = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';
    if (!apiBase) return;

    // Use sendBeacon when available (survives tab close), else fetch keepalive.
    const url = `${apiBase}/api/telemetry/language-detected`;
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => { /* swallow */ });
    }

    if (isFirstLoad) sessionStorage.setItem(SESSION_FLAG, '1');
  } catch (_) { /* noop — never break the app over telemetry */ }
}
// Defer slightly so we don't compete with first paint.
if (typeof window !== 'undefined') {
  setTimeout(trackLanguageDetection, 1500);
  i18n.on('languageChanged', () => setTimeout(trackLanguageDetection, 100));
}

export default i18n;
